import React from 'react';
import { 
  Wifi, 
  WifiOff, 
  CloudOff, 
  CheckCircle, 
  AlertCircle, 
  Loader2, 
  RefreshCw,
  X 
} from 'lucide-react';
import useOnlineStatus from '../hooks/useOnlineStatus';

/**
 * Banner component to show offline status and sync notifications
 */
const OfflineBanner = ({ 
  syncStatus, 
  syncMessage, 
  pendingCount = 0, 
  onSync, 
  onDismiss,
  onClearFailed
}) => {
  const isOnline = useOnlineStatus();

  // Don't show banner if online and no pending items and no sync status
  if (isOnline && pendingCount === 0 && syncStatus === 'idle') {
    return null;
  }

  const getBannerConfig = () => {
    // Offline state
    if (!isOnline) {
      return {
        type: 'offline',
        bgColor: 'bg-orange-100 border-orange-200',
        textColor: 'text-orange-800',
        icon: <WifiOff className="w-5 h-5 text-orange-600" />,
        title: 'Working Offline',
        message: pendingCount > 0 
          ? `${pendingCount} member${pendingCount !== 1 ? 's' : ''} saved offline - Will sync when reconnected`
          : 'You are currently offline. New members will be saved locally.',
        showSync: false
      };
    }

    // Sync status states
    switch (syncStatus) {
      case 'syncing':
        return {
          type: 'syncing',
          bgColor: 'bg-blue-100 border-blue-200',
          textColor: 'text-blue-800',
          icon: <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />,
          title: 'Syncing...',
          message: syncMessage || 'Syncing offline members to database...',
          showSync: false
        };

      case 'success':
        return {
          type: 'success',
          bgColor: 'bg-green-100 border-green-200',
          textColor: 'text-green-800',
          icon: <CheckCircle className="w-5 h-5 text-green-600" />,
          title: 'Sync Complete',
          message: syncMessage || 'All offline members have been synced successfully',
          showSync: false
        };

      case 'partial':
        return {
          type: 'partial',
          bgColor: 'bg-yellow-100 border-yellow-200',
          textColor: 'text-yellow-800',
          icon: <AlertCircle className="w-5 h-5 text-yellow-600" />,
          title: 'Partial Sync',
          message: syncMessage || 'Some members synced successfully, others failed',
          showSync: pendingCount > 0
        };

      case 'error':
        return {
          type: 'error',
          bgColor: 'bg-red-100 border-red-200',
          textColor: 'text-red-800',
          icon: <AlertCircle className="w-5 h-5 text-red-600" />,
          title: 'Sync Failed',
          message: syncMessage || 'Failed to sync offline members. Please try again.',
          showSync: pendingCount > 0
        };

      default:
        // Online with pending members
        if (pendingCount > 0) {
          return {
            type: 'pending',
            bgColor: 'bg-teal-100 border-teal-200',
            textColor: 'text-teal-800',
            icon: <CloudOff className="w-5 h-5 text-teal-600" />,
            title: 'Pending Sync',
            message: `${pendingCount} member${pendingCount !== 1 ? 's' : ''} waiting to be synced`,
            showSync: true
          };
        }

        return null;
    }
  };

  const config = getBannerConfig();
  if (!config) return null;

  return (
    <div className={`${config.bgColor} border rounded-lg p-4 mb-4 shadow-sm`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            {config.icon}
          </div>
          <div className="flex-1 min-w-0">
            <h4 className={`font-semibold ${config.textColor} text-sm`}>
              {config.title}
            </h4>
            <p className={`${config.textColor} text-sm mt-1 opacity-90`}>
              {config.message}
            </p>
            
            {/* Action buttons */}
            <div className="flex items-center gap-2 mt-3">
              {config.showSync && onSync && (
                <button
                  onClick={onSync}
                  className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    config.type === 'error' 
                      ? 'bg-red-200 text-red-800 hover:bg-red-300'
                      : 'bg-teal-200 text-teal-800 hover:bg-teal-300'
                  }`}
                >
                  <RefreshCw className="w-3 h-3" />
                  Sync Now
                </button>
              )}

              {/* Clear failed queue when in error state */}
              {config.type === 'error' && typeof onClearFailed === 'function' && pendingCount > 0 && (
                <button
                  onClick={onClearFailed}
                  className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-md bg-red-200 text-red-800 hover:bg-red-300 transition-colors"
                >
                  Okay (Remove)
                </button>
              )}
              
              {/* Connection status indicator */}
              <div className="flex items-center gap-1 text-xs opacity-75">
                {isOnline ? (
                  <>
                    <Wifi className="w-3 h-3 text-green-600" />
                    <span className={config.textColor}>Online</span>
                  </>
                ) : (
                  <>
                    <WifiOff className="w-3 h-3 text-orange-600" />
                    <span className={config.textColor}>Offline</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Dismiss button for success/error states */}
        {(config.type === 'success' || config.type === 'error' || config.type === 'partial') && onDismiss && (
          <button
            onClick={onDismiss}
            className={`flex-shrink-0 p-1 rounded-md transition-colors ${config.textColor} hover:bg-black hover:bg-opacity-10`}
            aria-label="Dismiss notification"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default OfflineBanner;
